import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { sel } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { CariPicker } from "@/components/common/CariPicker";
import SearchPicker, { fetchProducts } from "@/components/sales/SearchPicker";
import { TestBadge } from "@/components/common/TestBadge";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 25;
export const ALS_STATUS = { aktif: ["Aktif", "bg-emerald-50 text-emerald-700 border-emerald-200"], iptal: ["İptal", "bg-rose-50 text-rose-700 border-rose-200"] };
export const AlsBadge = ({ s, testid }) => <span className={`text-[11px] px-1.5 py-0.5 rounded border ${ALS_STATUS[s]?.[1]}`} data-testid={testid}>{ALS_STATUS[s]?.[0] || s}</span>;

function Filters({ f, setF, whs }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="flex flex-wrap gap-2 p-3 border-b border-slate-200 items-center" data-testid="purchases-filters">
      <Input className="h-9 w-48" placeholder="ALS no, fatura no, SKU…" value={f.q} onChange={set("q")} data-testid="purchases-search" />
      <Input type="date" className="h-9 w-40" value={f.date_from} onChange={set("date_from")} data-testid="purchases-date-from" />
      <Input type="date" className="h-9 w-40" value={f.date_to} onChange={set("date_to")} data-testid="purchases-date-to" />
      <div className="w-64"><CariPicker value={f.cari?.id} label={f.cari?.label} onChange={(c) => setF({ ...f, cari: c, page: 1 })} testid="purchases-cari" /></div>
      <div className="w-56">{f.product ? <button className="h-9 w-full border rounded px-2 text-left text-[13px] truncate bg-white" onClick={() => setF({ ...f, product: null, page: 1 })} title="Kaldır" data-testid="purchases-product-clear">{f.product.sku} · {f.product.name} ✕</button>
        : <SearchPicker placeholder="Ürün filtrele…" fetcher={fetchProducts} render={(p) => <span className="text-[13px]"><b className="font-mono">{p.sku}</b> {p.name}</span>} onPick={(p) => setF({ ...f, product: p, page: 1 })} testid="purchases-product" />}</div>
      <select className={sel} value={f.warehouse_id} onChange={set("warehouse_id")} data-testid="purchases-warehouse"><option value="">Tüm depolar</option>{whs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select>
      <select className={sel} value={f.status} onChange={set("status")} data-testid="purchases-status"><option value="">Tüm durumlar</option><option value="aktif">Aktif</option><option value="iptal">İptal</option></select>
    </div>
  );
}

export default function Purchases() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ q: "", date_from: "", date_to: "", cari: null, product: null, warehouse_id: "", status: "", page: 1 });
  const [res, setRes] = useState(null);
  const [whs, setWhs] = useState([]);
  useEffect(() => { api.get("/purchases/meta").then(({ data }) => setWhs(data.warehouses)); }, []);
  useEffect(() => {
    setRes(null);
    const { cari, product, ...rest } = f;
    const p = { ...rest, cari_id: cari?.id || "", product_id: product?.id || "", page_size: PAGE_SIZE };
    const t = setTimeout(() => api.get("/purchases", { params: p }).then(({ data }) => setRes(data)), 250);
    return () => clearTimeout(t);
  }, [f]);
  const hidden = res?.items?.[0]?.prices_hidden;
  return (
    <div data-testid="purchases-page">
      <PageHeader title="Alışlar" subtitle="Talep açmadan doğrudan alış · stok girişi, son alış fiyatı ve cari alacak kaydı" testid="purchases-title">
        {can("alislar", "create") && <Button onClick={() => navigate("/alislar/yeni")} className="bg-blue-600 hover:bg-blue-700" data-testid="purchases-new-button"><Plus className="h-4 w-4 mr-1" />Yeni Alış</Button>}
      </PageHeader>
      <div className="panel overflow-hidden">
        <Filters f={f} setF={setF} whs={whs} />
        <div className="overflow-x-auto">
          <table className="erp-table" data-testid="purchases-table">
            <thead><tr><th>Belge No</th><th>Tarih</th><th>Cari</th><th>Fatura No</th><th>Depo</th><th className="text-right">Kalem</th>{!hidden && <th className="text-right">Toplam (KDV dahil)</th>}<th>Ödeme</th><th>Durum</th></tr></thead>
            <tbody>{!res ? <LoadingRow cols={9} /> : res.items.length === 0 ? <EmptyRow cols={9} /> : res.items.map((p) => (
              <tr key={p.id} className="cursor-pointer" onClick={() => navigate(`/alislar/${p.id}`)} data-testid={`purchase-row-${p.no}`}>
                <td className="font-mono text-xs font-semibold">{p.no}<TestBadge show={p.is_test} testid={`purchase-test-${p.no}`} /></td>
                <td className="font-mono text-xs">{formatIsoDay(p.date)}</td><td className="max-w-[240px] truncate">{p.cari_code} {p.cari_title}</td>
                <td className="text-xs">{p.supplier_invoice_no || "—"}</td><td className="text-xs">{p.warehouse_name}</td><td className="text-right font-mono">{p.lines.length}</td>
                {!hidden && <td className="text-right font-mono font-semibold">{formatTRY(p.total_try)}</td>}
                <td className="text-xs">{p.payment_label}</td><td><AlsBadge s={p.status} testid={`purchase-status-${p.no}`} /></td>
              </tr>))}</tbody>
          </table>
        </div>
        {res && <Pager page={f.page} pageSize={PAGE_SIZE} total={res.total} onPage={(pg) => setF({ ...f, page: pg })} testid="purchases-pager" />}
      </div>
    </div>
  );
}
